import * as XLSX from "xlsx";
import { ROL_ADMIN, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL, NO_SUBTEL, NO_MINEDUC, REJECTED, QUESTIONS } from "../../Constants/Constants";

export const ROLES_CUMPLIMIENTO = [ROL_ADMIN, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL];
export const ROLES_FIRMAS_EXCEL = [ROL_ADMIN, ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL];

export const CUMPLE = "Cumple";
export const NO_CUMPLE = "No cumple";
export const SIN_DATOS = "Sin datos";

const METRICAS_PACTADAS = [
  { campo: "int_speed_down", etiqueta: "Bajada internacional", unidad: "Mbps", minimo: true },
  { campo: "speed_down", etiqueta: "Bajada", unidad: "Mbps", minimo: true },
  { campo: "speed_up", etiqueta: "Subida", unidad: "Mbps", minimo: true },
  { campo: "latency", etiqueta: "Latencia", unidad: "ms", minimo: false },
  { campo: "packet_loss", etiqueta: "Pérdida de paquetes", unidad: "%", minimo: false },
];

export function aNumero(valor) {
  if (valor === null || valor === undefined || valor === "") return null;
  const numero = Number(String(valor).trim().replace(",", "."));
  return Number.isFinite(numero) ? numero : null;
}

export function evaluarCumplimiento(medido = {}, pactado = {}) {
  const incumplimientos = [];
  let evaluadas = 0;

  METRICAS_PACTADAS.forEach(({ campo, etiqueta, unidad, minimo }) => {
    const valorPactado = aNumero(pactado?.[campo]);
    const valorMedido = aNumero(medido?.[campo]);
    if (valorPactado === null || valorMedido === null) return;

    evaluadas += 1;
    const incumple = minimo ? valorMedido < valorPactado : valorMedido > valorPactado;
    if (incumple) {
      incumplimientos.push(
        `${etiqueta}: ${valorMedido} ${unidad} (pactado ${minimo ? "mín." : "máx."} ${valorPactado} ${unidad})`
      );
    }
  });

  if (evaluadas === 0) return { cumplimiento: SIN_DATOS, detalle_incumplimiento: "" };
  if (incumplimientos.length > 0) {
    return { cumplimiento: NO_CUMPLE, detalle_incumplimiento: incumplimientos.join("; ") };
  }
  return { cumplimiento: CUMPLE, detalle_incumplimiento: "" };
}

export function formatearFechaHora(valor) {
  if (!valor) return "";
  return String(valor).replace("T", " ").slice(0, 16);
}

export function resumenFirma(firmantes, estado, estadoPendiente) {
  const nombres = (Array.isArray(firmantes) ? firmantes : [])
    .map((f) => {
      const nombre = String(f.nombre ?? "").trim();
      return nombre && f.por_ees ? `${nombre} (por EES)` : nombre;
    })
    .filter(Boolean);
  if (nombres.length > 0) return nombres.join(", ");
  return estado === estadoPendiente ? "Pendiente" : "";
}

export function datosFirmasExcel(row) {
  return {
    firma_subtel: resumenFirma(row.firmantes_subtel, row.estado, NO_SUBTEL),
    firma_mineduc: resumenFirma(row.firmantes_mineduc, row.estado, NO_MINEDUC),
  };
}

export function datosRechazoExcel(row) {
  if (row.estado !== REJECTED) return { motivo_rechazo: "", justificacion_rechazo: "" };
  const datos = row.datos ?? {};
  let motivo = datos.rejectOption ?? "";
  if (!motivo && Array.isArray(datos.answers)) {
    const negativas = QUESTIONS.filter((_, index) => datos.answers[index] === "no");
    if (negativas.length > 0) motivo = `Encuesta EES: ${negativas.join(" ")}`;
  }
  return {
    motivo_rechazo: motivo,
    justificacion_rechazo: String(datos.justificacion_subtel ?? "").trim(),
  };
}

export function compararValores(a, b, tipo = "string") {
  if (a == null || a === "") return b == null || b === "" ? 0 : 1;
  if (b == null || b === "") return -1;

  if (tipo === "number") {
    const na = aNumero(a);
    const nb = aNumero(b);
    if (na == null) return nb == null ? 0 : 1;
    if (nb == null) return -1;
    return na - nb;
  }

  return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
}

export function ordenarFilas(filas, columnas, orderBy, order) {
  if (!orderBy) return filas;
  const columna = columnas.find((c) => c.id === orderBy);
  const campo = columna?.sortKey || orderBy;
  const tipo = columna?.sortType || "string";
  const dir = order === "asc" ? 1 : -1;

  return filas
    .map((fila, indice) => ({ fila, indice }))
    .sort((r1, r2) => {
      const a = r1.fila[campo];
      const b = r2.fila[campo];
      const vacioA = a == null || a === "";
      const vacioB = b == null || b === "";
      if (vacioA !== vacioB) return vacioA ? 1 : -1;
      const res = compararValores(a, b, tipo);
      if (res !== 0) return res * dir;
      return r1.indice - r2.indice;
    })
    .map(({ fila }) => fila);
}

export function columnasExcelDocumento(tipo, verCumplimiento, verFirmas) {
  const sufijo = tipo.toUpperCase();
  const columnas = [
    { campo: "id", titulo: `ID ${sufijo}`, ancho: 10 },
    { campo: "folio", titulo: "Folio", ancho: 12 },
    { campo: "rbd", titulo: "RBD", ancho: 10 },
    { campo: "periodo", titulo: "Periodo", ancho: 10 },
    { campo: "fecha", titulo: "Fecha", ancho: 12 },
    { campo: "fecha_creacion", titulo: "Fecha creación", ancho: 18 },
    { campo: "operador", titulo: "Operador", ancho: 24 },
    { campo: "rut_instalador", titulo: "RUT instalador", ancho: 15 },
    { campo: "estado", titulo: "Estado", ancho: 22 },
    { campo: "estado_resumen", titulo: "Estado resumen", ancho: 16 },
    { campo: "motivo_rechazo", titulo: "Motivo de rechazo", ancho: 50 },
    { campo: "justificacion_rechazo", titulo: "Justificación del rechazo", ancho: 50 },
    { campo: "url_pdf", titulo: "URL PDF", ancho: 40 },
    { campo: "temporal", titulo: "Representante temporal", ancho: 28 },
    { campo: "tipo_tecnologia", titulo: "Tipo tecnología", ancho: 20 },
    { campo: "bajada_internacional_pactada", titulo: "Bajada internacional pactada (Mbps)", ancho: 20 },
    { campo: "bajada_pactada", titulo: "Bajada pactada (Mbps)", ancho: 16 },
    { campo: "subida_pactada", titulo: "Subida pactada (Mbps)", ancho: 16 },
    { campo: "latencia_pactada", titulo: "Latencia pactada (ms)", ancho: 16 },
    { campo: "perdida_de_paquetes_pactada", titulo: "Pérdida de paquetes pactada (%)", ancho: 20 },
    { campo: "bajada_internacional_operador", titulo: `Bajada internacional operador ${sufijo} (Mbps)`, ancho: 22 },
    { campo: "bajada_operador", titulo: `Bajada operador ${sufijo} (Mbps)`, ancho: 18 },
    { campo: "subida_operador", titulo: `Subida operador ${sufijo} (Mbps)`, ancho: 18 },
    { campo: "latencia_operador", titulo: `Latencia operador ${sufijo} (ms)`, ancho: 18 },
    { campo: "perdida_de_paquetes_operador", titulo: `Pérdida de paquetes operador ${sufijo} (%)`, ancho: 22 },
  ];

  if (verFirmas) {
    columnas.push(
      { campo: "firma_subtel", titulo: "Firma SUBTEL", ancho: 28 },
      { campo: "firma_mineduc", titulo: "Firma MINEDUC", ancho: 28 }
    );
  }

  if (verCumplimiento) {
    columnas.push(
      { campo: "cumplimiento", titulo: "Cumple lo pactado", ancho: 16 },
      { campo: "detalle_incumplimiento", titulo: "Detalle incumplimiento", ancho: 60 }
    );
  }

  return columnas;
}

export function filaExcelDocumento(row, { id, tipoTecnologia, pactado }) {
  const medido = row.datos ?? {};
  return {
    id,
    folio: row.folio,
    rbd: row.rbd,
    periodo: row.periodo,
    fecha: row.fecha,
    fecha_creacion: formatearFechaHora(row.fecha_creacion),
    operador: row.operador,
    rut_instalador: row.rut_instalador,
    estado: row.estado,
    estado_resumen: row.estado_resumen,
    ...datosRechazoExcel(row),
    url_pdf: row.url || row.ruta_url || "",
    temporal: row.temporal,
    tipo_tecnologia: tipoTecnologia ?? "",
    bajada_internacional_pactada: aNumero(pactado.int_speed_down),
    bajada_pactada: aNumero(pactado.speed_down),
    subida_pactada: aNumero(pactado.speed_up),
    latencia_pactada: aNumero(pactado.latency),
    perdida_de_paquetes_pactada: aNumero(pactado.packet_loss),
    bajada_internacional_operador: aNumero(medido.int_speed_down),
    bajada_operador: aNumero(medido.speed_down),
    subida_operador: aNumero(medido.speed_up),
    latencia_operador: aNumero(medido.latency),
    perdida_de_paquetes_operador: aNumero(medido.packet_loss),
    ...datosFirmasExcel(row),
    cumplimiento: row.cumplimiento,
    detalle_incumplimiento: row.detalle_incumplimiento,
  };
}

export function exportarExcel({ nombreArchivo, nombreHoja, columnas, filas }) {
  const datos = [
    columnas.map((c) => c.titulo),
    ...filas.map((fila) => columnas.map((c) => fila[c.campo] ?? "")),
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(datos);
  worksheet["!cols"] = columnas.map((c) => ({ wch: c.ancho ?? 18 }));
  worksheet["!rows"] = [{ hpt: 30 }];
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: filas.length, c: columnas.length - 1 } }),
  };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, nombreHoja);
  XLSX.writeFile(workbook, nombreArchivo);
}
